"use client";

import * as React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "vo2-max-definition",
  question: "What is VO₂ max?",
  answer: "VO₂ max is the maximum volume of oxygen your body can transport and utilise during intense, progressive exercise. It serves as a benchmark measurement of cardiorespiratory and aerobic fitness."
}, {
  id: "athletes-only",
  question: "Is VO₂ max only useful for athletes?",
  answer: "No. It can be useful for recreational exercisers and people wanting an objective fitness measure, as well as competitive athletes."
}, {
  id: "test-duration",
  question: "How long does the test take?",
  answer: "The active graded exercise portion is commonly around 10–15 minutes with CardioCoach, while the complete PhysAnalytica appointment is longer for preparation, recovery and explanation."
}, {
  id: "exercise-intensity",
  question: "Will I have to exercise as hard as possible?",
  answer: "The protocol progressively increases intensity, and the intended endpoint depends on the testing protocol, participant response and safety."
}, {
  id: "heart-disease-diagnosis",
  question: "Does the test diagnose heart disease?",
  answer: "No. It is a fitness/performance assessment rather than a diagnostic cardiac examination."
}, {
  id: "rmr-definition",
  question: "What is RMR?",
  answer: "It is the measured resting energy expenditure."
}, {
  id: "rmr-vs-daily-calories",
  question: "Is RMR the same as my daily calorie requirement?",
  answer: "No. RMR represents resting energy requirements. Total daily expenditure also includes activity, exercise and other factors."
}, {
  id: "measure-o2-co2",
  question: "Why measure both oxygen and carbon dioxide during exercise?",
  answer: "The CardioCoach MAX uses O₂ and CO₂ information to provide respiratory exchange information and estimate relative fat/carbohydrate utilisation during exercise."
}, {
  id: "heart-rate-zones",
  question: "Can I use the results to set heart-rate zones?",
  answer: "The CardioCoach assessment provides personalised exercise-zone information based on the measured exercise response."
}, {
  id: "repeat-testing",
  question: "Can I repeat the test?",
  answer: "Yes. Repeat testing under similar conditions can help track changes over time."
}, {
  id: "testing-safety",
  question: "Is testing safe?",
  answer: "A pre-test screening and readiness process is conducted prior to assessment. Testing may be postponed or declined where maximal exercise is inappropriate."
}];
export default function FaqAccordion() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="faqaccordion" data-nav="light" ref={ref} className="bg-background text-foreground py-16 md:py-24 transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-10 md:mb-14 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
            Frequently asked questions
          </h2>
          <p className="mt-3 text-base md:text-lg text-muted-foreground">
            Essential details regarding VO₂ max, metabolic testing, and appointment expectations.
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-xs">
          <Accordion type="single" collapsible className="w-full">
            {FAQ_ITEMS.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="border-b border-border/80 last:border-b-0 py-1">
                <AccordionTrigger className="text-left font-medium text-foreground hover:text-primary py-4 text-base md:text-lg">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm md:text-base leading-relaxed pt-1 pb-4">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}